import { useState } from 'react';
import {
  Activity,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Bell,
  Database,
  Server,
  Shield,
  FileText,
  CreditCard,
  Coins,
  Users,
  Clock,
  Eye,
  CheckCheck,
} from 'lucide-react';
import {
  useSystemHealth,
  useDatabaseHealth,
  useSystemAlerts,
  useAlertStats,
  useCriticalAlerts,
  useAcknowledgeAlert,
  useResolveAlert,
  HealthStatus,
  AlertSeverity,
  AlertModule,
  SystemAlert,
  ModuleHealth,
  getStatusColor,
  getSeverityColor,
  formatModuleName,
} from '../../hooks/useSystemHealth';

// ============================================
// COMPONENTS
// ============================================

function StatusBadge({ status }: { status: HealthStatus }) {
  const colorClass = getStatusColor(status);
  const Icon = status === 'HEALTHY' ? CheckCircle : status === 'WARNING' ? AlertTriangle : XCircle;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${colorClass}`}>
      <Icon className="w-3 h-3" />
      {status === 'HEALTHY' ? 'Saudável' : status === 'WARNING' ? 'Atenção' : 'Crítico'}
    </span>
  );
}

function SeverityBadge({ severity }: { severity: AlertSeverity }) {
  const colorClass = getSeverityColor(severity);
  
  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colorClass}`}>
      {severity}
    </span>
  );
}

function ModuleIcon({ module }: { module: string }) {
  const icons: Record<string, React.ReactNode> = {
    tokenization: <Coins className="w-5 h-5" />,
    banking: <CreditCard className="w-5 h-5" />,
    credit: <CreditCard className="w-5 h-5" />,
    kyc: <Users className="w-5 h-5" />,
    contracts: <FileText className="w-5 h-5" />,
    security: <Shield className="w-5 h-5" />,
    infrastructure: <Server className="w-5 h-5" />,
  };
  
  return icons[module.toLowerCase()] || <Activity className="w-5 h-5" />;
}

function ModuleCard({ module, health }: { module: string; health: ModuleHealth }) {
  const statusColors: Record<HealthStatus, string> = {
    HEALTHY: 'border-green-200 bg-green-50',
    WARNING: 'border-yellow-200 bg-yellow-50',
    CRITICAL: 'border-red-200 bg-red-50',
  };
  
  return (
    <div className={`p-4 rounded-lg border-2 ${statusColors[health.status]}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ModuleIcon module={module} />
          <h3 className="font-semibold">{formatModuleName(module)}</h3>
        </div>
        <StatusBadge status={health.status} />
      </div>
      
      <div className="space-y-2 text-sm">
        {Object.entries(health.metrics).slice(0, 4).map(([key, value]) => (
          <div key={key} className="flex justify-between">
            <span className="text-gray-600">{formatMetricName(key)}</span>
            <span className="font-medium">{formatMetricValue(key, value)}</span>
          </div>
        ))}
      </div>
      
      {health.alerts > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <div className="flex items-center gap-1 text-orange-600">
            <Bell className="w-4 h-4" />
            <span className="text-sm font-medium">{health.alerts} alertas ativos</span>
          </div>
        </div>
      )}
      
      <div className="mt-2 text-xs text-gray-500">
        Última verificação: {new Date(health.lastCheck).toLocaleTimeString('pt-BR')}
      </div>
    </div>
  );
}

function AlertCard({ 
  alert, 
  onAcknowledge, 
  onResolve 
}: { 
  alert: SystemAlert; 
  onAcknowledge: (id: string) => void;
  onResolve: (id: string) => void;
}) {
  return (
    <div className="p-4 bg-white rounded-lg border shadow-sm">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <SeverityBadge severity={alert.severity} />
            <span className="text-xs text-gray-500 uppercase">{alert.module}</span>
          </div>
          <h4 className="font-semibold text-gray-900">{alert.title}</h4>
          <p className="text-sm text-gray-600 mt-1">{alert.message}</p>
          
          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {new Date(alert.createdAt).toLocaleString('pt-BR')}
            </span>
            {alert.acknowledged && (
              <span className="flex items-center gap-1 text-blue-600">
                <Eye className="w-3 h-3" />
                Reconhecido
              </span>
            )}
            {alert.resolvedAt && (
              <span className="flex items-center gap-1 text-green-600">
                <CheckCheck className="w-3 h-3" />
                Resolvido
              </span>
            )}
          </div>
        </div>
        
        <div className="flex gap-2 ml-4">
          {!alert.acknowledged && (
            <button
              onClick={() => onAcknowledge(alert.id)}
              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Reconhecer"
            >
              <Eye className="w-4 h-4" />
            </button>
          )}
          {!alert.resolvedAt && (
            <button
              onClick={() => onResolve(alert.id)}
              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
              title="Resolver"
            >
              <CheckCheck className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function formatMetricName(key: string): string {
  const names: Record<string, string> = {
    totalAssets: 'Total de Ativos',
    pendingReview: 'Pendentes de Revisão',
    avgCoverageRatio: 'Cobertura Média',
    undercollateralizedAssets: 'Subcapitalizados',
    pendingYieldPayments: 'Yields Pendentes',
    failedYieldPayments: 'Yields Falhos',
    pixTransactions24h: 'PIX (24h)',
    pixFailureRate: 'Taxa de Falha PIX',
    pendingTransactions: 'Transações Pendentes',
    stuckTransactions: 'Transações Travadas',
    totalLoans: 'Total de Empréstimos',
    activeLoans: 'Empréstimos Ativos',
    overdueLoans: 'Empréstimos Vencidos',
    defaultRate: 'Taxa de Inadimplência',
    pendingApprovals: 'Aprovações Pendentes',
    pendingSubmissions: 'Submissões Pendentes',
    approvalRate: 'Taxa de Aprovação',
    rejectionRate: 'Taxa de Rejeição',
    pendingSignatures: 'Assinaturas Pendentes',
    expiredContracts: 'Contratos Expirados',
    stuckContracts: 'Contratos Travados',
    completionRate: 'Taxa de Conclusão',
    failedLogins24h: 'Logins Falhos (24h)',
    blockedAccounts: 'Contas Bloqueadas',
  };
  return names[key] || key;
}

function formatMetricValue(key: string, value: number): string {
  if (key.includes('Rate') || key.includes('Ratio')) {
    return `${value.toFixed(1)}%`;
  }
  return value.toLocaleString('pt-BR');
}

// ============================================
// MAIN COMPONENT
// ============================================

export function SystemHealthDashboard() {
  const [alertFilter, setAlertFilter] = useState<{
    module?: AlertModule;
    severity?: AlertSeverity;
    resolved?: boolean;
  }>({});
  
  const { data: health, isLoading: healthLoading, refetch: refetchHealth } = useSystemHealth();
  const { data: dbHealth } = useDatabaseHealth();
  const { data: alertStats } = useAlertStats();
  const { data: criticalAlerts } = useCriticalAlerts();
  const { data: alerts, isLoading: alertsLoading } = useSystemAlerts(alertFilter);
  
  const acknowledgeAlert = useAcknowledgeAlert();
  const resolveAlert = useResolveAlert();
  
  const handleAcknowledge = (alertId: string) => {
    acknowledgeAlert.mutate(alertId);
  };
  
  const handleResolve = (alertId: string) => {
    resolveAlert.mutate({ alertId });
  };
  
  if (healthLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Saúde do Sistema</h1>
          <p className="text-gray-600">Monitoramento em tempo real de todos os módulos</p>
        </div>
        <button
          onClick={() => refetchHealth()}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Atualizar
        </button>
      </div>
      
      {/* Overall Status */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className={`p-6 rounded-lg border-2 ${
          health?.overallStatus === 'HEALTHY' ? 'border-green-300 bg-green-50' :
          health?.overallStatus === 'WARNING' ? 'border-yellow-300 bg-yellow-50' :
          'border-red-300 bg-red-50'
        }`}>
          <div className="flex items-center gap-3">
            <Activity className="w-8 h-8" />
            <div>
              <p className="text-sm text-gray-600">Status Geral</p>
              <StatusBadge status={health?.overallStatus || 'HEALTHY'} />
            </div>
          </div>
        </div>
        
        <div className="p-6 rounded-lg border bg-white">
          <div className="flex items-center gap-3">
            <Database className={`w-8 h-8 ${dbHealth?.status === 'HEALTHY' ? 'text-green-600' : 'text-red-600'}`} />
            <div>
              <p className="text-sm text-gray-600">Banco de Dados</p>
              <p className="font-semibold">
                {dbHealth?.status === 'HEALTHY' ? 'Online' : 'Offline'}
                {dbHealth?.responseTime && ` (${dbHealth.responseTime}ms)`}
              </p>
            </div>
          </div>
        </div>
        
        <div className="p-6 rounded-lg border bg-white">
          <div className="flex items-center gap-3">
            <Bell className="w-8 h-8 text-orange-600" />
            <div>
              <p className="text-sm text-gray-600">Alertas Ativos</p>
              <p className="font-semibold text-2xl">{alertStats?.unresolved || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="p-6 rounded-lg border bg-white">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-8 h-8 text-red-600" />
            <div>
              <p className="text-sm text-gray-600">Alertas Críticos</p>
              <p className="font-semibold text-2xl">{health?.criticalAlerts || 0}</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Critical Alerts Banner */}
      {criticalAlerts && criticalAlerts.length > 0 && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <h3 className="font-semibold text-red-800">Alertas Críticos Ativos</h3>
          </div>
          <div className="space-y-2">
            {criticalAlerts.slice(0, 3).map((alert) => (
              <div key={alert.id} className="flex items-center justify-between p-2 bg-white rounded border border-red-100">
                <div>
                  <span className="font-medium">{alert.title}</span>
                  <span className="text-sm text-gray-600 ml-2">({alert.module})</span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleAcknowledge(alert.id)}
                    className="text-sm text-blue-600 hover:underline"
                  >
                    Reconhecer
                  </button>
                  <button
                    onClick={() => handleResolve(alert.id)}
                    className="text-sm text-green-600 hover:underline"
                  >
                    Resolver
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Module Health Grid */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Saúde por Módulo</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {health?.modules && Object.entries(health.modules).map(([module, moduleHealth]) => (
            <ModuleCard key={module} module={module} health={moduleHealth} />
          ))}
        </div>
      </div>
      
      {/* Alert Stats */}
      {alertStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-4 bg-white rounded-lg border">
            <h3 className="font-semibold mb-3">Alertas por Módulo</h3>
            <div className="space-y-2">
              {Object.entries(alertStats.byModule).filter(([, count]) => count > 0).map(([module, count]) => (
                <div key={module} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{module}</span>
                  <span className="font-medium">{count}</span>
                </div>
              ))}
              {Object.values(alertStats.byModule).every(v => v === 0) && (
                <p className="text-sm text-gray-500">Nenhum alerta por módulo</p>
              )}
            </div>
          </div>
          
          <div className="p-4 bg-white rounded-lg border">
            <h3 className="font-semibold mb-3">Alertas por Severidade</h3>
            <div className="space-y-2">
              {Object.entries(alertStats.bySeverity).filter(([, count]) => count > 0).map(([severity, count]) => (
                <div key={severity} className="flex items-center justify-between">
                  <SeverityBadge severity={severity as AlertSeverity} />
                  <span className="font-medium">{count}</span>
                </div>
              ))}
              {Object.values(alertStats.bySeverity).every(v => v === 0) && (
                <p className="text-sm text-gray-500">Nenhum alerta por severidade</p>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Alert List */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Todos os Alertas</h2>
          <div className="flex gap-2">
            <select
              value={alertFilter.module || ''}
              onChange={(e) => setAlertFilter(prev => ({ ...prev, module: e.target.value as AlertModule || undefined }))}
              className="px-3 py-1 border rounded-lg text-sm"
            >
              <option value="">Todos os Módulos</option>
              <option value="TOKENIZATION">Tokenização</option>
              <option value="BANKING">Banking</option>
              <option value="CREDIT">Crédito</option>
              <option value="KYC">KYC</option>
              <option value="CONTRACTS">Contratos</option>
              <option value="SECURITY">Segurança</option>
            </select>
            <select
              value={alertFilter.severity || ''}
              onChange={(e) => setAlertFilter(prev => ({ ...prev, severity: e.target.value as AlertSeverity || undefined }))}
              className="px-3 py-1 border rounded-lg text-sm"
            >
              <option value="">Todas as Severidades</option>
              <option value="CRITICAL">Crítico</option>
              <option value="HIGH">Alto</option>
              <option value="MEDIUM">Médio</option>
              <option value="LOW">Baixo</option>
              <option value="INFO">Info</option>
            </select>
            <select
              value={alertFilter.resolved === undefined ? '' : alertFilter.resolved ? 'true' : 'false'}
              onChange={(e) => setAlertFilter(prev => ({ 
                ...prev, 
                resolved: e.target.value === '' ? undefined : e.target.value === 'true' 
              }))}
              className="px-3 py-1 border rounded-lg text-sm"
            >
              <option value="">Todos</option>
              <option value="false">Não Resolvidos</option>
              <option value="true">Resolvidos</option>
            </select>
          </div>
        </div>
        
        {alertsLoading ? (
          <div className="flex items-center justify-center h-32">
            <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
          </div>
        ) : alerts && alerts.length > 0 ? (
          <div className="space-y-3">
            {alerts.map((alert) => (
              <AlertCard
                key={alert.id}
                alert={alert}
                onAcknowledge={handleAcknowledge}
                onResolve={handleResolve}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Bell className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>Nenhum alerta encontrado</p>
          </div>
        )}
      </div>
      
      {/* Last Updated */}
      {health?.lastUpdated && (
        <div className="text-center text-sm text-gray-500">
          Última atualização: {new Date(health.lastUpdated).toLocaleString('pt-BR')}
        </div>
      )}
    </div>
  );
}

export default SystemHealthDashboard;
